/* =============================================================
   AUTH — SESSION STORE
   =============================================================
   A module-level reactive singleton rather than provide/inject,
   for the same reason as the main app: the router guard runs
   outside any component and has to read `status` before deciding
   where to send a visitor.

   The thing that makes this different from the app's store is
   that a Supabase session is NOT sufficient here. Anyone with a
   River Atlas account can authenticate against this project —
   they just cannot do anything once they are in, because RLS
   refuses them. Signing them into a panel where every button
   returns 42501 is a bad experience and an information leak, so
   `authenticated` here means *authenticated AND in
   platform_admins*, and a non-admin is signed straight back out.

   None of this is a security boundary. It is the UI agreeing
   with the database about who is allowed in, so the two never
   tell the user different stories. The boundary is RLS.
   ============================================================= */

import { computed, ref, shallowRef } from 'vue'
import { supabase } from './supabase'
import type { Admin } from './types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

const admin = shallowRef<Admin | null>(null)
const status = ref<AuthStatus>('loading')
const isAuthenticated = computed(() => status.value === 'authenticated')

let ready: Promise<void> | null = null

/** Thrown by `signIn` so the form renders one message shape. */
export class SignInError extends Error {
  readonly code: string
  constructor(message: string, code = 'sign_in_failed') {
    super(message)
    this.name = 'SignInError'
    this.code = code
  }
}

/**
 * The signed-in user's `platform_admins` row, or null.
 *
 * RLS returns an empty result for a non-admin rather than an error — the
 * policy only exposes your own row — so "no row" and "not an admin" are
 * the same answer, which is exactly what this needs to distinguish.
 */
async function loadAdmin(): Promise<Admin | null> {
  const { data: sessionData } = await supabase.auth.getSession()
  const user = sessionData.session?.user
  if (!user) return null

  const { data, error } = await supabase
    .from('platform_admins')
    .select('user_id, name')
    .maybeSingle()

  // A failed lookup is not proof of non-admin — it could be the network.
  // Treat it as not-admin for access purposes but say so rather than
  // silently pretending the person does not exist.
  if (error) throw new SignInError('Could not verify admin access. Try again.', 'lookup_failed')
  if (!data) return null

  return { userId: data.user_id, email: user.email ?? '', name: data.name }
}

function apply(next: Admin | null): void {
  admin.value = next
  status.value = next ? 'authenticated' : 'anonymous'
}

/** Restore any existing session. Safe to call more than once. */
export function initAuth(): Promise<void> {
  if (ready) return ready

  ready = loadAdmin()
    .then(async (found) => {
      // A stored session belonging to a non-admin (revoked, or a customer
      // account reused here) must not leave a half-signed-in client around.
      if (!found) await supabase.auth.signOut()
      apply(found)
    })
    .catch(() => {
      apply(null)
    })

  // supabase-js warns against awaiting inside this callback — doing so can
  // deadlock the client's internal lock — so the re-check is dispatched
  // outside it. Fires on token refresh, sign-out, and other tabs.
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      apply(null)
      return
    }
    if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
      setTimeout(() => {
        void loadAdmin()
          .then(apply)
          .catch(() => {
            apply(null)
          })
      }, 0)
    }
  })

  return ready
}

/** Resolves once the initial session restore has settled. */
export function authReady(): Promise<void> {
  return ready ?? initAuth()
}

export function useAuth() {
  return {
    status,
    admin,
    isAuthenticated,

    async signIn(email: string, password: string): Promise<void> {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      })

      if (error) {
        throw new SignInError(
          error.message === 'Invalid login credentials'
            ? 'Wrong email or password.'
            : error.message,
          'invalid_credentials',
        )
      }

      const found = await loadAdmin().catch(async (e: unknown) => {
        await supabase.auth.signOut()
        throw e
      })

      if (!found) {
        // Correct password, wrong person. Do not leave them holding a
        // session for a panel they cannot use.
        await supabase.auth.signOut()
        apply(null)
        throw new SignInError(
          'That account is not a River Atlas admin.',
          'not_an_admin',
        )
      }

      apply(found)
    },

    async signOut(): Promise<void> {
      await supabase.auth.signOut()
      apply(null)
    },
  }
}
